import { useMemo, useState } from 'react'
import { FiLink, FiLogOut } from 'react-icons/fi'
import { GoOrganization } from 'react-icons/go'
import { PiDatabase, PiTreeStructure, PiUserCircle, PiUsers } from 'react-icons/pi'
import { RxLockClosed } from 'react-icons/rx'
import { MenuDropdown, MenuDropdownItem } from '~/@/components/inputs/selection/menu-dropdown'
import config from '~/common/config/config'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useSignOutHandler } from '~/common/hooks/useSignOutHandler'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { useCanViewDataSources } from '~/components/data-sources/useCanViewDataSources'
import { SendEmailDialog } from '../shared/SendEmailDialog'
import { UserAvatar } from '../users/UserAvatar'
import { useConfigbaseNavEnabled } from './configbase-nav/useConfigbaseNavEnabled'

export const LeftNavAccountMenu = () => {
  const { t } = useTypedTranslation()
  const { user, isAdmin, isInternal } = useGlobalContext()
  const [openEmailDialog, setOpenEmailDialog] = useState(false)
  const handleSignOut = useSignOutHandler()
  const canViewDataSources = useCanViewDataSources()
  const { isEnabled: isConfigbaseNavEnabled, setIsEnabled: setIsConfigbaseNavEnabled } =
    useConfigbaseNavEnabled()

  const usersTabDisplay =
    isAdmin || isInternal ? t('settings.manage_users') : t('settings.organization')

  const emailBodyAdmin = `${t('email_body_admin.admin_details')}
- ${t('email_body_admin.requested_by')} ${user?.firstName} ${user?.lastName} (${user?.email})
- ${t('email_body_admin.organization_id')} ${user?.organizationId}
- ${t('email_body_admin.url')} ${typeof window !== 'undefined' ? window.location.href : ''}`

  const menuItems: MenuDropdownItem[] = useMemo(() => {
    const items: MenuDropdownItem[] = [
      {
        label: t('settings.account.title'),
        icon: <PiUserCircle className="text-base" />,
        href: '/settings',
      },
      {
        label: usersTabDisplay,
        icon:
          isAdmin || isInternal ? (
            <PiUsers className="text-base" />
          ) : (
            <GoOrganization className="text-base" />
          ),
        href: '/settings/members',
      },
    ]

    if (isAdmin || isInternal) {
      items.push({
        label: t('settings.roles'),
        icon: <RxLockClosed className="text-base" />,
        href: '/settings/roles',
      })
    }

    if (isInternal) {
      items.push({
        label: t('settings.configbase_nav'),
        icon: <PiTreeStructure className="text-base" />,
        toggle: { isChecked: isConfigbaseNavEnabled, onChange: setIsConfigbaseNavEnabled },
      })
    }

    items.push({
      label: t('settings.integrations'),
      icon: <FiLink className="text-base" />,
      href: '/settings/connections',
    })

    if (canViewDataSources) {
      items.push({
        label: t('settings.data_sources'),
        icon: <PiDatabase className="text-base" />,
        href: '/settings/data-sources',
      })
    }

    items.push(
      { separator: true, label: '' },
      {
        label: t('login.logout'),
        icon: <FiLogOut className="text-base" />,
        onClick: handleSignOut,
      },
    )

    return items
  }, [
    t,
    usersTabDisplay,
    isAdmin,
    isInternal,
    canViewDataSources,
    handleSignOut,
    isConfigbaseNavEnabled,
    setIsConfigbaseNavEnabled,
  ])

  if (!user) {
    return null
  }

  return (
    <>
      <MenuDropdown
        align="end"
        items={menuItems}
        menuClassName="w-60"
        side="right"
        sideOffset={0}
        subtitle={user.email ?? undefined}
        title={`${user.firstName} ${user.lastName}`}
        trigger={
          <div className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 hover:bg-gray-2">
            <UserAvatar small user={user} />
            <span className="min-w-0 truncate text-sm text-gray-12">
              {user.firstName} {user.lastName}
            </span>
          </div>
        }
      />
      <SendEmailDialog
        defaultBody={t('send_email_dialog.default_body')}
        defaultSubject={`[${user.organizationName}] ${t('send_email_dialog.default_subject')}`}
        defaultTo={config.supportEmail}
        emailBodyAdmin={emailBodyAdmin}
        onClose={() => setOpenEmailDialog(false)}
        open={openEmailDialog}
        title={t('send_email_dialog.title')}
      />
    </>
  )
}
